import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { API_BASE } from '../api-base';
import { apiFetch } from '../api-client';
import { useMe } from '../me';
import { queryClient, queryKeys } from '../query-client';
import { calendarOwnerKey } from './calendar-storage';

interface CalendarTitlePreference {
  showEventTitles: boolean;
}

async function readPreference(): Promise<CalendarTitlePreference> {
  const response = await apiFetch(`${API_BASE}/api/calendar/title-preference`, { credentials: 'include' });
  if (!response.ok) throw new Error(`calendar title preference ${response.status}`);
  return response.json();
}

async function writePreference(showEventTitles: boolean): Promise<CalendarTitlePreference> {
  const response = await apiFetch(`${API_BASE}/api/calendar/title-preference`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ showEventTitles }),
  });
  if (!response.ok) throw new Error(`calendar title preference ${response.status}`);
  return response.json();
}

export function useCalendarTitlePreference() {
  const ownerKey = calendarOwnerKey(useMe());
  const query = useQuery({
    queryKey: queryKeys.calendarTitlePreference(ownerKey),
    queryFn: readPreference,
    enabled: !!ownerKey,
    staleTime: Infinity,
  });
  const [saving, setSaving] = useState(false);

  const setShowEventTitles = useCallback(
    async (showEventTitles: boolean) => {
      const key = queryKeys.calendarTitlePreference(ownerKey);
      const previous = queryClient.getQueryData<CalendarTitlePreference>(key);
      queryClient.setQueryData<CalendarTitlePreference>(key, { showEventTitles });
      setSaving(true);
      try {
        queryClient.setQueryData<CalendarTitlePreference>(key, await writePreference(showEventTitles));
        void queryClient.invalidateQueries({ queryKey: queryKeys.calendarMeetings(ownerKey), refetchType: 'active' });
      } catch (error) {
        queryClient.setQueryData<CalendarTitlePreference | undefined>(key, previous);
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [ownerKey],
  );

  return {
    showEventTitles: query.data?.showEventTitles !== false,
    loading: query.isLoading || saving,
    setShowEventTitles,
  };
}
